import React, { useMemo, useState, useCallback } from 'react';
import { motion } from 'motion/react';
import { getSoldRows, getUnsoldRows, getIncludedSold, getTotalProfit, getLivePrice, allocateCents } from './helpers';
import { findSkinImage } from './skinIndex';

// Overview 7 — "THE PROFIT ZONE". Neo-brutalist zine board: candy colors,
// 3px black borders, hard offset shadows, rotated stickers, a waffle chart,
// sticky-note feed and a starburst win-rate badge.
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const CANDY = ['#ffd43d', '#ff9bd2', '#6fe8cf', '#b79cff', '#c9f65a', '#ffab5e', '#fffef8'];

const COND_ABBR = {
  'Factory New': 'FN', 'Minimal Wear': 'MW', 'Field Tested': 'FT',
  'Well Worn': 'WW', 'Battle Scarred': 'BS',
};

const fmt = (n) => {
  const abs = Math.abs(n);
  return abs >= 1000 ? abs.toLocaleString('en-US', { maximumFractionDigits: 0 }) : abs.toFixed(2);
};
const signed = (n) => (n >= 0 ? '+$' : '-$') + fmt(n);

function getItemTypes(row) {
  if (!row?.itemType) return [];
  return Array.isArray(row.itemType) ? row.itemType : [row.itemType];
}


const drop = (i, rot = 0) => ({
  initial: { opacity: 0, y: -26, rotate: rot + (i % 2 ? 3 : -3) },
  animate: { opacity: 1, y: 0, rotate: rot },
  transition: { type: 'spring', stiffness: 320, damping: 22, delay: 0.06 * i },
});

export default function OverviewTab7({ rows, settings, livePrices, skinsData = [] }) {
  const [chartYear, setChartYear]         = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(null);

  const isExcluded = useCallback(r => getItemTypes(r).some(t => {
    const def = settings?.itemTypes?.find(it => it.value === t.value);
    return def?.overrideProfitLoss === true;
  }), [settings?.itemTypes]); // eslint-disable-line react-hooks/exhaustive-deps

  const availableYears = useMemo(() => {
    const years = new Set();
    rows?.forEach(r => { const y = new Date(r.timestamp).getFullYear(); if (y) years.add(y); });
    return [...years].sort((a, b) => b - a);
  }, [rows]);

  // ── Stats ──────────────────────────────────────────────────
  const stats = useMemo(() => {
    if (!rows?.length) return {};
    const unsold = getUnsoldRows(rows);
    const totalProfit = getTotalProfit(rows, isExcluded);
    const unsoldCost  = unsold.reduce((s, r) => s + (parseFloat(r.buyPrice) || 0), 0);

    let unrealizedPnl = 0, marketValue = 0;
    unsold.forEach(r => {
      const lp  = getLivePrice(livePrices, r);
      const buy = parseFloat(r.buyPrice) || 0;
      if (lp) {
        const market = lp.starting_at?.price || 0;
        marketValue   += market;
        unrealizedPnl += market - buy;
      } else marketValue += buy;
    });

    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 30);
    const profit30d = getIncludedSold(rows, isExcluded)
      .filter(r => new Date(r.timestamp) >= cutoff)
      .reduce((s, r) => s + (parseFloat(r.profit) || 0), 0);

    return {
      itemCount: rows.filter(r => r.skin?.value).length,
      soldCount: getSoldRows(rows).length,
      unsoldCount: unsold.length,
      unsoldCost, marketValue, unrealizedPnl, totalProfit, profit30d,
      unrealizedRoi: unsoldCost > 0 ? unrealizedPnl / unsoldCost * 100 : null,
    };
  }, [rows, livePrices, isExcluded]);

  const perf = useMemo(() => {
    const sold = getIncludedSold(rows || [], isExcluded).filter(r => r.skin?.value);
    if (!sold.length) return null;
    const profits = sold.map(r => parseFloat(r.profit) || 0);
    const wins = profits.filter(p => p > 0).length;
    let best = sold[0], worst = sold[0];
    sold.forEach(r => {
      const p = parseFloat(r.profit) || 0;
      if (p > (parseFloat(best.profit)  || 0)) best  = r;
      if (p < (parseFloat(worst.profit) || 0)) worst = r;
    });
    return { count: sold.length, wins, winRate: Math.round(wins / sold.length * 100),
      avg: profits.reduce((s, p) => s + p, 0) / sold.length, best, worst };
  }, [rows, isExcluded]);

  // ── Monthly bars ───────────────────────────────────────────
  const monthly = useMemo(() => {
    const months = MONTHS.map((label, i) => ({ label, month: i + 1, profit: 0, count: 0 }));
    rows?.filter(r => parseFloat(r.sellPrice) > 0 && !isExcluded(r)).forEach(r => {
      const d = new Date(r.timestamp);
      if (d.getFullYear() === chartYear) {
        const m = months[d.getMonth()];
        m.profit += parseFloat(r.profit) || 0;
        m.count  += 1;
      }
    });
    return months.map(m => ({ ...m, profit: parseFloat(m.profit.toFixed(2)) }));
  }, [rows, chartYear, isExcluded]); // eslint-disable-line react-hooks/exhaustive-deps

  const maxAbs = useMemo(() => Math.max(1, ...monthly.map(m => Math.abs(m.profit))), [monthly]);

  const monthFlips = useMemo(() => {
    if (!selectedMonth || !rows) return [];
    return rows
      .filter(r => {
        const d = new Date(r.timestamp);
        return parseFloat(r.sellPrice) > 0 && d.getFullYear() === chartYear && d.getMonth() + 1 === selectedMonth;
      })
      .sort((a, b) => parseFloat(b.profit) - parseFloat(a.profit));
  }, [rows, selectedMonth, chartYear]);

  // ── Waffle (100 cells, largest-remainder via allocateCents) ─
  const waffle = useMemo(() => {
    if (!rows) return { cats: [], cells: [] };
    const liveRows = rows.filter(r => r.skin?.value && parseFloat(r.buyPrice) > 0);
    const typed = (settings?.itemTypes || [])
      .filter(t => t.value !== 'Normal')
      .map(type => ({
        name: type.label,
        value: liveRows
          .filter(r => getItemTypes(r).some(t => t.value === type.value))
          .reduce((s, r) => s + (parseFloat(r.buyPrice) || 0), 0),
      }))
      .filter(d => d.value > 0);
    const uncat = liveRows.filter(r => getItemTypes(r).length === 0)
      .reduce((s, r) => s + (parseFloat(r.buyPrice) || 0), 0);
    if (uncat > 0) typed.push({ name: 'Other stuff', value: uncat });
    typed.sort((a, b) => b.value - a.value);
    if (!typed.length) return { cats: [], cells: [] };

    const shares = allocateCents(typed.map(d => d.value), 100);
    const cats = typed.map((d, i) => ({ ...d, cells: shares[i], color: CANDY[i % CANDY.length] }));
    const cells = [];
    cats.forEach((c, ci) => { for (let k = 0; k < c.cells; k++) cells.push(ci); });
    return { cats, cells };
  }, [rows, settings]);

  // ── Loot / feed ────────────────────────────────────────────
  const loot = useMemo(() => {
    if (!rows) return [];
    return getUnsoldRows(rows)
      // getUnsoldRows, not a hand-rolled sellPrice test: an item traded away
      // keeps an empty sellPrice forever and would still be listed as a holding.
      .filter(r => parseFloat(r.buyPrice) > 0)
      .sort((a, b) => parseFloat(b.buyPrice) - parseFloat(a.buyPrice))
      .slice(0, 5);
  }, [rows]);

  const feed = useMemo(() => {
    if (!rows) return [];
    return [...rows]
      .filter(r => r.skin?.value)
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .slice(0, 5);
  }, [rows]);

  const noData = !rows?.filter(r => r.skin?.value).length;
  if (noData) {
    return (
      <div className="ov7-root" style={{ textAlign: 'center', padding: '70px 30px' }}>
        <div className="ov7-title"><span className="hl">EMPTY ZONE</span></div>
        <p style={{ fontWeight: 700, marginTop: 22 }}>No loot yet. Go add some items in the Tracker!</p>
      </div>
    );
  }

  const NOTE_COLORS = ['var(--ov7-yellow)', 'var(--ov7-pink)', 'var(--ov7-teal)', 'var(--ov7-lime)', 'var(--ov7-purple)'];
  const bestP  = perf ? parseFloat(perf.best.profit)  || 0 : 0;
  const worstP = perf ? parseFloat(perf.worst.profit) || 0 : 0;

  return (
    <div className="ov7-root">

      {/* ── Header ─────────────────────────────────── */}
      <div className="ov7-header">
        <motion.div className="ov7-title" {...drop(0)}>
          THE <span className="hl">PROFIT</span> ZONE
        </motion.div>
        <motion.span className="ov7-sticker" style={{ background: 'var(--ov7-teal)', transform: 'rotate(2deg)' }} {...drop(1, 2)}>
          {stats.itemCount} items · 100% real data
        </motion.span>
        <motion.span className="ov7-sticker" style={{ background: 'var(--ov7-lime)', transform: 'rotate(-2deg)' }} {...drop(2, -2)}>
          {livePrices ? 'prices: LIVE ✷' : 'prices: loading…'}
        </motion.span>
        {perf && (
          <motion.div className="ov7-burst-wrap" {...drop(3)}>
            <div className="ov7-burst" />
            <div className="ov7-burst-label">
              <b>{perf.winRate}%</b>
              <span>win rate</span>
            </div>
          </motion.div>
        )}
      </div>

      {/* ── Stats ──────────────────────────────────── */}
      <div className="ov7-stats">
        {[
          { label: 'Net worth', value: `$${fmt(stats.marketValue ?? 0)}`, sub: `${stats.unsoldCount} items in the vault`, bg: 'var(--ov7-yellow)', rot: -1.2 },
          { label: 'Cash spent', value: `$${fmt(stats.unsoldCost ?? 0)}`, sub: 'cost of everything held', bg: 'var(--ov7-white)', rot: 0.8 },
          { label: 'Paper gains', value: signed(stats.unrealizedPnl ?? 0),
            sub: stats.unrealizedRoi != null ? `${stats.unrealizedRoi >= 0 ? '+' : ''}${stats.unrealizedRoi.toFixed(1)}% on cost` : '—',
            bg: (stats.unrealizedPnl ?? 0) >= 0 ? 'var(--ov7-teal)' : 'var(--ov7-pink)', rot: -0.6 },
          { label: 'Banked profit', value: signed(stats.totalProfit ?? 0), sub: `${signed(stats.profit30d ?? 0)} in the last 30 days`,
            bg: (stats.totalProfit ?? 0) >= 0 ? 'var(--ov7-lime)' : 'var(--ov7-hot)', rot: 1.1 },
        ].map((s, i) => (
          <motion.div key={s.label} className="ov7-stat" style={{ background: s.bg, transform: `rotate(${s.rot}deg)` }} {...drop(i + 4, s.rot)}>
            <span className="ov7-stat-label">{s.label}</span>
            <div className="ov7-stat-value">{s.value}</div>
            <div className="ov7-stat-sub">{s.sub}</div>
          </motion.div>
        ))}
      </div>

      {/* ── Chart + waffle ─────────────────────────── */}
      <div className="ov7-main-grid" style={{ display: 'grid', gridTemplateColumns: '1.7fr 1fr', gap: 22, alignItems: 'start', marginBottom: 26 }}>

        <motion.div className="ov7-card ov7-chart-card" {...drop(8)}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div className="ov7-panel-title">Monthly damage report</div>
            <div style={{ marginLeft: 'auto', marginTop: -26, display: 'flex', gap: 8 }}>
              {availableYears.map(y => (
                <button key={y} className={`ov7-year-btn ${chartYear === y ? 'active' : ''}`}
                  onClick={() => { setChartYear(y); setSelectedMonth(null); }}>
                  {y}
                </button>
              ))}
            </div>
          </div>
          <div className="ov7-bars">
            {monthly.map(m => {
              const h   = Math.max(m.profit !== 0 ? 8 : 0, Math.round(Math.abs(m.profit) / maxAbs * (m.profit >= 0 ? 124 : 56)));
              const neg = m.profit < 0;
              return (
                <div key={m.month}
                  className={`ov7-bar-col ${selectedMonth === m.month ? 'sel' : ''}`}
                  onClick={() => setSelectedMonth(selectedMonth === m.month ? null : m.month)}
                  title={`${m.label} ${chartYear}: ${signed(m.profit)} (${m.count} flips)`}>
                  <div className="ov7-bar-up">
                    {!neg && m.profit !== 0 && (
                      <motion.div className="ov7-bar pos"
                        initial={{ height: 0 }} animate={{ height: h }}
                        transition={{ type: 'spring', stiffness: 260, damping: 24, delay: 0.3 + m.month * 0.04 }} />
                    )}
                  </div>
                  <div className="ov7-bar-axis" />
                  <div className="ov7-bar-down">
                    {neg && (
                      <motion.div className="ov7-bar neg"
                        initial={{ height: 0 }} animate={{ height: h }}
                        transition={{ type: 'spring', stiffness: 260, damping: 24, delay: 0.3 + m.month * 0.04 }} />
                    )}
                  </div>
                  <div className="ov7-bar-label">{m.label}</div>
                  <div className="ov7-bar-val" style={{ color: m.profit === 0 ? 'rgba(19,19,19,0.35)' : m.profit > 0 ? '#0d7a43' : 'var(--ov7-hot)' }}>
                    {m.profit !== 0 ? signed(m.profit) : '·'}
                  </div>
                </div>
              );
            })}
          </div>

          {selectedMonth && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              style={{ marginTop: 14, borderTop: '3px solid var(--ov7-black)', paddingTop: 10, fontSize: 15, fontWeight: 700 }}>
              {MONTHS[selectedMonth - 1]} {chartYear}:{' '}
              {monthFlips.length === 0 ? 'nothing sold. quiet month!' : monthFlips.map((r, i) => {
                const p = parseFloat(r.profit) || 0;
                return (
                  <span key={i}>
                    {r.skin?.value} <span style={{ color: p >= 0 ? '#0d7a43' : 'var(--ov7-hot)' }}>({signed(p)})</span>
                    {i < monthFlips.length - 1 ? ' · ' : ''}
                  </span>
                );
              })}
            </motion.div>
          )}
        </motion.div>

        <motion.div className="ov7-card" style={{ padding: '36px 18px 18px' }} {...drop(9)}>
          <div className="ov7-panel-title" style={{ transform: 'rotate(1deg)' }}>The pie, but square</div>
          {waffle.cats.length === 0 ? (
            <p style={{ fontWeight: 700, fontSize: 15.5 }}>Nothing categorised yet — every cell is empty.</p>
          ) : (
            <>
              <div className="ov7-waffle">
                {waffle.cells.map((ci, i) => (
                  <motion.span key={i} style={{ background: waffle.cats[ci].color }}
                    initial={{ scale: 0 }} animate={{ scale: 1 }}
                    transition={{ delay: 0.35 + i * 0.006, type: 'spring', stiffness: 400, damping: 22 }} />
                ))}
              </div>
              <div className="ov7-legend">
                {waffle.cats.map((c, i) => (
                  <div key={i} className="ov7-legend-row">
                    <span className="ov7-legend-chip" style={{ background: c.color }} />
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
                    <span>{c.cells}%</span>
                    <span style={{ opacity: 0.6 }}>${fmt(c.value)}</span>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 10, fontSize: 14.5, fontWeight: 700, opacity: 0.5 }}>1 square = 1% of invested cash</div>
            </>
          )}
        </motion.div>
      </div>

      {/* ── Loot ───────────────────────────────────── */}
      <motion.div style={{ marginBottom: 26 }} {...drop(10)}>
        <div className="ov7-panel-title" style={{ margin: '0 0 22px 4px' }}>Top loot — biggest buys</div>
        <div className="ov7-loot-grid">
          {loot.map((r, i) => {
            const img  = findSkinImage(skinsData, r.skin?.value, r.condition?.value);
            const lp   = getLivePrice(livePrices, r);
            const mkt  = lp?.starting_at?.price;
            const buy  = parseFloat(r.buyPrice) || 0;
            const diff = mkt ? mkt - buy : null;
            return (
              <motion.div key={i} className="ov7-card press ov7-loot"
                style={{ transform: `rotate(${(i % 2 ? 1 : -1) * (0.8 + i * 0.2)}deg)` }}
                {...drop(11 + i, (i % 2 ? 1 : -1) * (0.8 + i * 0.2))}>
                <div className="ov7-price-tag">${fmt(buy)}</div>
                {img && <img className="skin" src={img} alt="" />}
                <div className="ov7-loot-name">{r.skin?.value}</div>
                <div className="ov7-loot-cond">{COND_ABBR[r.condition?.value] || r.condition?.value || '?'}</div>
                {diff !== null && (
                  <span className="ov7-delta-badge" style={{ background: diff >= 0 ? 'var(--ov7-teal)' : 'var(--ov7-pink)' }}>
                    {diff >= 0 ? '▲' : '▼'} {signed(diff)}
                  </span>
                )}
              </motion.div>
            );
          })}
        </div>
      </motion.div>

      {/* ── Feed + W/L ─────────────────────────────── */}
      <div className="ov7-bottom-grid" style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: 22, alignItems: 'start' }}>

        <motion.div {...drop(16)}>
          <div className="ov7-panel-title" style={{ margin: '0 0 24px 4px', transform: 'rotate(-1.5deg)' }}>Fresh off the board</div>
          <div className="ov7-notes">
            {feed.map((r, i) => {
              const sold   = parseFloat(r.sellPrice) > 0;
              const profit = parseFloat(r.profit) || 0;
              return (
                <motion.div key={i} className="ov7-note"
                  style={{ background: NOTE_COLORS[i % NOTE_COLORS.length], transform: `rotate(${(i % 2 ? 1 : -1) * 0.9}deg)` }}
                  {...drop(17 + i, (i % 2 ? 1 : -1) * 0.9)}>
                  {sold
                    ? <>Flipped <b>{r.skin?.value}</b> for <span className="amt" style={{ color: profit >= 0 ? '#0d7a43' : 'var(--ov7-hot)' }}>{signed(profit)}</span></>
                    : <>Grabbed <b>{r.skin?.value}</b> for <span className="amt">${fmt(parseFloat(r.buyPrice) || 0)}</span> — still holding</>}
                  <span className="when">{new Date(r.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {perf && (
          <motion.div className="ov7-wl" {...drop(22)}>
            <div className="ov7-panel-title" style={{ margin: '0 0 4px 4px', transform: 'rotate(1deg)' }}>Hall of fame / shame</div>
            <div className="ov7-card ov7-wl-card" style={{ background: 'var(--ov7-lime)', transform: 'rotate(-0.8deg)' }}>
              <div className="ov7-wl-letter">W</div>
              <div style={{ minWidth: 0 }}>
                <div className="ov7-wl-name">{perf.best.skin?.value}</div>
                <div className="ov7-wl-amt" style={{ color: '#0d7a43' }}>{signed(bestP)}</div>
              </div>
            </div>
            <div className="ov7-card ov7-wl-card" style={{ background: 'var(--ov7-pink)', transform: 'rotate(0.9deg)' }}>
              <div className="ov7-wl-letter">L</div>
              <div style={{ minWidth: 0 }}>
                <div className="ov7-wl-name">{perf.worst.skin?.value}</div>
                <div className="ov7-wl-amt" style={{ color: worstP >= 0 ? '#0d7a43' : 'var(--ov7-hot)' }}>{signed(worstP)}</div>
              </div>
            </div>
            <div className="ov7-card" style={{ padding: '12px 16px', fontWeight: 800, fontSize: 15.5, transform: 'rotate(-0.5deg)' }}>
              {perf.count} flips total · avg {signed(perf.avg)} each · {perf.wins}W / {perf.count - perf.wins}L
            </div>
          </motion.div>
        )}
      </div>

      {/* ── Footer ─────────────────────────────────── */}
      <div className="ov7-footer">
        <span className="ov7-sticker" style={{ background: 'var(--ov7-purple)', transform: 'rotate(-1deg)' }}>overview no. 07</span>
        <span className="ov7-sticker" style={{ background: 'var(--ov7-white)', transform: 'rotate(1.5deg)' }}>prices via buff163</span>
        <span className="ov7-sticker" style={{ background: 'var(--ov7-orange)', transform: 'rotate(-2deg)', marginLeft: 'auto' }}>
          {stats.soldCount} sold ✦ {stats.unsoldCount} holding
        </span>
      </div>
    </div>
  );
}
